import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { Paper } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import { Close as CloseIcon } from "@material-ui/icons";
import { Table, Button, Labelbox, Modal } from "../../components";
import { Toaster } from "../../utils/common";
import EfilingSignedConsentsDisplay from "../../components/EfilingSignedConsentsDisplay";
import EfilingDocumentPreviewModal from "../../components/EfilingDocumentPreviewModal";
import {
  fetchClientEfilings,
  createClientEfiling,
  updateClientEfiling,
} from "../../utils/efilingApi";
import {
  canEditEfilingRecord,
  canEditEfilingStatus,
} from "../../utils/efilingAccess";
import {
  recordToEditForm,
  validateEditEfilingForm,
  validateEfilingStatusForm,
  buildEfilingUpdatePayload,
  buildEfilingStatusUpdatePayload,
} from "../../utils/efilingFormUtils";
import {
  emptyBusinessEfilingForm,
  recordToBusinessEditForm,
  validateBusinessEfilingForm,
  buildBusinessEfilingPayload,
  resolveEntityName,
} from "../../utils/businessEfilingFormUtils";
import {
  buildBusinessOfficeInvoiceDropdown,
  findBusinessOfficeDocument,
  getOfficeDocumentLink,
  getOfficeDocumentName,
  listBusinessOfficeDocuments,
} from "../../utils/businessEfilingOfficeDocuments";
import EfilingEditModal from "../../components/EfilingEditModal";
import EfilingStatusEditModal from "../../components/EfilingStatusEditModal";
import EfilingConsentField from "../../components/EfilingConsentField";
import BusinessEfilingFormFields from "../../components/BusinessEfilingFormFields";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { DOCUMENT_ACTION } from "../../redux/action";

const PAYMENT_METHOD_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Direct Deposit", value: "Direct Deposit" },
  // API stores "Check"; UI label is "Paper Check"
  { id: "Check", value: "Paper Check" },
  { id: "Online Payment", value: "Online Payment" },
  { id: "Direct Debit", value: "Direct Debit" },
];

const FILING_STATUS_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Efiled", value: "Efiled" },
  { id: "Pending", value: "Pending" },
  { id: "Rejected", value: "Rejected" },
];

const CLIENT_MGR_VERIFIED_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Yes", value: "Yes" },
  { id: "No", value: "No" },
];

const STATUS_TABS = [
  { id: "All", label: "All" },
  { id: "Pending", label: "Pending" },
  { id: "Efiled", label: "Efiled" },
  { id: "Rejected", label: "Rejected" },
];

const EFILING_REQUIRED_MSG = "This field is required";

function trimField(value) {
  return String(value ?? "").trim();
}

function validateCreateEfilingForm(form, clientFirst, clientLast) {
  const errors = {};

  if (!trimField(form.filing_date)) errors.filing_date = EFILING_REQUIRED_MSG;
  if (!trimField(form.invoice_no)) errors.invoice_no = EFILING_REQUIRED_MSG;
  if (!trimField(clientLast)) errors.client_last = EFILING_REQUIRED_MSG;
  if (!trimField(clientFirst)) errors.client_first = EFILING_REQUIRED_MSG;
  if (!trimField(form.fed_due_ref)) errors.fed_due_ref = EFILING_REQUIRED_MSG;
  if (!trimField(form.fed_payment_method)) {
    errors.fed_payment_method = EFILING_REQUIRED_MSG;
  }
  if (!trimField(form.state_due_ref)) errors.state_due_ref = EFILING_REQUIRED_MSG;
  if (!trimField(form.state_payment_method)) {
    errors.state_payment_method = EFILING_REQUIRED_MSG;
  }
  if (!trimField(form.filer_comment)) errors.filer_comment = EFILING_REQUIRED_MSG;
  if (!trimField(form.client_mgr_verified)) {
    errors.client_mgr_verified = EFILING_REQUIRED_MSG;
  }
  if (!trimField(form.ack_status)) errors.ack_status = EFILING_REQUIRED_MSG;

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

function formatMoneyDisplay(num) {
  const n = Number(num);
  if (Number.isNaN(n)) return "";
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function yesNoDisplay(val) {
  if (val === "Yes" || val === "No") return val;
  if (val === 1 || val === "1" || val === true || val === "true") return "Yes";
  if (val === 0 || val === "0" || val === false || val === "false")
    return "No";
  const s = String(val ?? "").trim().toLowerCase();
  if (!s) return "";
  if (["yes", "y"].includes(s)) return "Yes";
  if (["no", "n"].includes(s)) return "No";
  return String(val);
}

function parseMoney(val) {
  if (val === null || val === undefined || val === "") return 0;
  const s = String(val).replace(/[$,\s]/g, "").trim();
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

function invoiceTotalAmount(inv) {
  if (!inv) return 0;
  const raw =
    inv.total ??
    inv.net_total ??
    inv.NetTotal ??
    inv.amount ??
    inv.invoice_total ??
    inv.invoice_amount ??
    inv.sub_total ??
    inv.gross_total;
  return parseMoney(raw);
}

function invoicePaidAmount(inv) {
  if (!inv) return 0;
  return parseMoney(
    inv.amount_paid ?? inv.amountPaid ?? inv.paid_amount ?? 0,
  );
}

function findInvoiceForSelect(clientInvoices, selectedValue) {
  const sv = String(selectedValue ?? "").trim();
  if (!sv) return null;
  const list = clientInvoices || [];
  for (let idx = 0; idx < list.length; idx++) {
    const inv = list[idx];
    const ids = [
      inv.invoice_no,
      inv.invoiceNo,
      inv.invoice_number,
      inv.client_invoice_id,
    ]
      .filter((x) => x != null && x !== "")
      .map((x) => String(x).trim());
    if (ids.some((id) => id === sv)) return inv;
    if (sv === `idx-${idx}`) return inv;
  }
  return null;
}

function derivePaymentFromInvoice(inv) {
  if (!inv) {
    return {
      paymentStatus: "",
      paymentMethod: "",
      invoiceAmountDisplay: "",
    };
  }
  const totalNum = invoiceTotalAmount(inv);
  const amountPaidNum = invoicePaidAmount(inv);
  const amountDue = Math.max(totalNum - amountPaidNum, 0);

  let paymentStatus = (
    inv.payment_status ??
    inv.paymentStatus ??
    inv.pay_status ??
    ""
  )
    .toString()
    .trim();

  if (!paymentStatus) {
    if (totalNum <= 0 && amountPaidNum <= 0) paymentStatus = "";
    else if (amountDue === 0 && totalNum > 0) paymentStatus = "Paid";
    else if (amountPaidNum > 0 && amountPaidNum < totalNum)
      paymentStatus = "Partially Paid";
    else paymentStatus = "Pending";
  }

  const paymentMethod =
    inv.payment_method ||
    inv.paymentMethod ||
    inv.payment_type ||
    "";

  const invoiceAmountDisplay =
    totalNum > 0
      ? formatMoneyDisplay(totalNum)
      : String(
          inv.total ??
            inv.net_total ??
            inv.amount ??
            inv.invoice_total ??
            "",
        ).trim();

  return {
    paymentStatus,
    paymentMethod: paymentMethod ? String(paymentMethod) : "",
    invoiceAmountDisplay,
  };
}

function resolveClientNames(clientData) {
  if (!clientData) return { first: "", last: "" };
  const first =
    clientData.first_name ??
    clientData.firstName ??
    clientData.primary_first_name ??
    "";
  const last =
    clientData.last_name ??
    clientData.lastName ??
    clientData.primary_last_name ??
    "";
  return { first: String(first || ""), last: String(last || "") };
}

function resolvePreparer(activeEmployees, loggedInUserId) {
  const employeeIdLs = localStorage.getItem("employee_id");
  const emailLs = (localStorage.getItem("email") || "").trim();
  const firstLs = localStorage.getItem("first_name");
  const lastLs = localStorage.getItem("last_name");

  const emp = (activeEmployees || []).find((e) => {
    const eid = e?.employee_id;
    const uid = e?.user_id;
    const em = String(e?.email || "").toLowerCase();
    return (
      String(eid) === String(loggedInUserId) ||
      String(eid) === String(employeeIdLs) ||
      String(uid) === String(loggedInUserId) ||
      (emailLs && em === emailLs.toLowerCase())
    );
  });

  if (emp) {
    const name = `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
    return {
      preparerDisplay: name || emailLs || String(loggedInUserId || ""),
      preparerEmployeeId:
        emp.employee_id ?? emp.user_id ?? employeeIdLs ?? loggedInUserId,
    };
  }

  try {
    const u = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
    const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
    if (name) {
      return {
        preparerDisplay: name,
        preparerEmployeeId:
          u.employee_id ?? employeeIdLs ?? loggedInUserId,
      };
    }
  } catch (_e) {
    /* ignore */
  }

  const fromLs = `${firstLs || ""} ${lastLs || ""}`.trim();
  if (fromLs) {
    return {
      preparerDisplay: fromLs,
      preparerEmployeeId: employeeIdLs || loggedInUserId,
    };
  }

  return {
    preparerDisplay: emailLs || String(loggedInUserId || "—"),
    preparerEmployeeId: employeeIdLs || loggedInUserId,
  };
}

function renderEfilingStatusBadge(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const slug = value.toLowerCase().replace(/\s+/g, "");
  return (
    <span className={`status-badge status-${slug}`}>{value}</span>
  );
}

const emptyForm = () => ({
  filing_date: moment().format("YYYY-MM-DD"),
  invoice_no: "",
  fed_due_ref: "",
  fed_payment_method: "",
  state_due_ref: "",
  state_payment_method: "",
  filing_status: "Pending",
  filer_comment: "",
  signed_consents_link: "",
  client_mgr_verified: "",
  other_comments: "",
  ack_status: "",
  payment_status_readonly: "",
  payment_method_readonly: "",
  invoice_amount_readonly: "",
});

function EfilingReadonlyField({ label, value, required, error, errmsg }) {
  const v = value || "";
  return (
    <TextField
      fullWidth
      variant="outlined"
      label={label}
      value={v}
      required={required}
      error={error}
      helperText={error ? errmsg : ""}
      InputProps={{ readOnly: true }}
      margin="dense"
      InputLabelProps={{ shrink: true }}
    />
  );
}

export default function EfilingSection({
  clientId,
  clientData,
  activeEmployees,
  loggedInUserId,
  clientInvoices,
  ensureClientInvoices,
  category: categoryProp,
  onViewInvoice,
}) {
  const dispatch = useDispatch();
  const allClientDocuments = useSelector(
    (state) => state.document?.allClientDocuments || [],
  );
  const [internalCategoryTab, setInternalCategoryTab] = useState("individual");
  const categoryTab = categoryProp ?? internalCategoryTab;
  const showCategoryTabs = categoryProp == null;
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [editFormErrors, setEditFormErrors] = useState({});
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [statusEditOpen, setStatusEditOpen] = useState(false);
  const [statusEditSaving, setStatusEditSaving] = useState(false);
  const [statusEditRow, setStatusEditRow] = useState(null);
  const [statusEditForm, setStatusEditForm] = useState({
    filing_status: "Pending",
    other_comments: "",
  });
  const [statusEditFormErrors, setStatusEditFormErrors] = useState({});
  const [officeDocPreview, setOfficeDocPreview] = useState({
    open: false,
    url: "",
    documentName: "",
    clientDocumentId: "",
  });

  const canEditStatus = canEditEfilingStatus();
  const showEditAction = canEditEfilingRecord();

  const clearFormError = useCallback((field) => {
    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const preparerInfo = useMemo(
    () => resolvePreparer(activeEmployees, loggedInUserId),
    [activeEmployees, loggedInUserId],
  );

  const { first: clientFirst, last: clientLast } = useMemo(
    () => resolveClientNames(clientData),
    [clientData],
  );

  const isBusiness = categoryTab === "business";

  useEffect(() => {
    if (!isBusiness || !clientId) return;
    dispatch(
      DOCUMENT_ACTION.getAllClientDocuments({
        user_id: clientId,
        client_id: clientId,
        document_category: "business",
      }),
    );
  }, [isBusiness, clientId, dispatch]);

  const officeDocuments = useMemo(
    () => (isBusiness ? listBusinessOfficeDocuments(allClientDocuments) : []),
    [isBusiness, allClientDocuments],
  );

  const isCreateFormValid = useMemo(() => {
    if (isBusiness) {
      return validateBusinessEfilingForm(form, {
        skipStatusAndFilerComment: false,
      }).valid;
    }
    return validateCreateEfilingForm(form, clientFirst, clientLast).valid;
  }, [form, clientFirst, clientLast, isBusiness]);

  const invoiceDropdown = useMemo(() => {
    if (isBusiness) {
      return buildBusinessOfficeInvoiceDropdown(
        officeDocuments,
        form.invoice_no || editForm.invoice_no,
      );
    }
    const list = clientInvoices || [];
    const opts = [
      { id: "", value: "Select invoice", keepAtTop: true },
      ...list.map((inv, idx) => {
        const no =
          inv.invoice_no ??
          inv.invoiceNo ??
          inv.invoice_number ??
          inv.client_invoice_id ??
          "";
        const amt = formatMoneyDisplay(inv.total);
        const idVal = String(no || `idx-${idx}`);
        return {
          id: idVal,
          value: `${no || idVal}${amt ? ` — $${amt}` : ""}`,
        };
      }),
    ];
    return opts;
  }, [clientInvoices, isBusiness, officeDocuments, form.invoice_no, editForm.invoice_no]);

  const loadRecords = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const rows = await fetchClientEfilings(clientId, categoryTab);
      setRecords(Array.isArray(rows) ? rows : []);
    } finally {
      setLoading(false);
    }
  }, [clientId, categoryTab]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    setCreateOpen(false);
    setStatusFilter("Pending");
  }, [categoryTab]);

  const filteredRecords = useMemo(() => {
    const list =
      statusFilter === "All"
        ? [...(records || [])]
        : (records || []).filter((r) => {
            const status = String(r.filing_status ?? r.status ?? "").trim();
            return status === statusFilter;
          });

    const ts = (r) => {
      const d =
        r.filing_date ||
        r.date ||
        r.created_at ||
        r.createdAt ||
        r.updated_at ||
        "";
      const t = new Date(d).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    return list.sort((a, b) => ts(a) - ts(b));
  }, [records, statusFilter]);

  const isClientManager = useMemo(
    () => Boolean(clientData?.sales && String(clientData.sales) === String(loggedInUserId)),
    [clientData, loggedInUserId]
  );

  const tableActions = useMemo(() => {
    const list = [];
    if (showEditAction) list.push("edit");
    if (canEditStatus || isClientManager) list.push("editStatus");
    return list;
  }, [showEditAction, canEditStatus, isClientManager]);

  const openCreate = () => {
    if (categoryTab === "business") {
      setForm({
        ...emptyBusinessEfilingForm(),
        entity_name: resolveEntityName(clientData),
      });
    } else {
      setForm(emptyForm());
    }
    setFormErrors({});
    setCreateOpen(true);
  };

  useEffect(() => {
    if (isBusiness) return;
    if (
      (createOpen || editOpen) &&
      typeof ensureClientInvoices === "function"
    ) {
      ensureClientInvoices();
    }
  }, [createOpen, editOpen, ensureClientInvoices, isBusiness]);

  const resolvedInvoice = useMemo(
    () =>
      isBusiness
        ? null
        : findInvoiceForSelect(clientInvoices, form.invoice_no),
    [clientInvoices, form.invoice_no, isBusiness],
  );

  const editResolvedInvoice = useMemo(
    () =>
      isBusiness
        ? null
        : findInvoiceForSelect(clientInvoices, editForm.invoice_no),
    [clientInvoices, editForm.invoice_no, isBusiness],
  );

  const selectedOfficeDocument = useMemo(
    () =>
      isBusiness
        ? findBusinessOfficeDocument(officeDocuments, form.invoice_no)
        : null,
    [isBusiness, officeDocuments, form.invoice_no],
  );

  const editSelectedOfficeDocument = useMemo(
    () =>
      isBusiness
        ? findBusinessOfficeDocument(officeDocuments, editForm.invoice_no)
        : null,
    [isBusiness, officeDocuments, editForm.invoice_no],
  );

  const applyInvoiceSelection = useCallback(
    (raw, target = "create") => {
      const key = raw != null ? String(raw).trim() : "";
      if (isBusiness) {
        const updater = (prev) => ({
          ...prev,
          invoice_no: key,
        });
        if (target === "edit") setEditForm(updater);
        else setForm(updater);
        return;
      }
      const inv = findInvoiceForSelect(clientInvoices, key);
      const d = derivePaymentFromInvoice(inv);
      const updater = (prev) => ({
        ...prev,
        invoice_no: key,
        invoice_amount_readonly: d.invoiceAmountDisplay,
        payment_status_readonly: d.paymentStatus,
        payment_method_readonly: d.paymentMethod,
        invoice_status: d.paymentStatus,
      });
      if (target === "edit") setEditForm(updater);
      else setForm(updater);
    },
    [clientInvoices, isBusiness],
  );

  useEffect(() => {
    if (isBusiness) return;
    if (!createOpen || !String(form.invoice_no || "").trim()) return;
    const inv = findInvoiceForSelect(clientInvoices, form.invoice_no);
    const d = derivePaymentFromInvoice(inv);
    setForm((prev) => {
      if (
        prev.invoice_amount_readonly === d.invoiceAmountDisplay &&
        prev.payment_status_readonly === d.paymentStatus &&
        prev.payment_method_readonly === d.paymentMethod &&
        prev.invoice_status === d.paymentStatus
      ) {
        return prev;
      }
      return {
        ...prev,
        invoice_amount_readonly: d.invoiceAmountDisplay,
        payment_status_readonly: d.paymentStatus,
        payment_method_readonly: d.paymentMethod,
        invoice_status: d.paymentStatus,
      };
    });
  }, [createOpen, clientInvoices, form.invoice_no, isBusiness]);

  const tableHeader = useMemo(() => {
    const headers =
      categoryTab === "business"
        ? [
            { id: "date", label: "Date" },
            { id: "preparer", label: "Preparer" },
            { id: "entity_name", label: "Entity Name" },
            { id: "business_type", label: "Type" },
            { id: "state_filing", label: "State filing" },
            { id: "federal_due", label: "Federal Due" },
            { id: "fed_payment_method", label: "Fed Payment Method" },
            { id: "state_due", label: "State Due" },
            { id: "state_payment_method", label: "State Payment Method" },
            { id: "ca_estimate_payment", label: "CA estimate payment" },
            { id: "ca_payment_method", label: "CA Payment Method" },
            { id: "invoice", label: "Invoice Number" },
            { id: "invoice_amount", label: "Invoice Amount" },
            { id: "invoice_status", label: "Invoice Status" },
            { id: "filer_comment", label: "Preparer Comments" },
            { id: "signed_consents_link", label: "Signed Consent Link" },
            { id: "password", label: "Password" },
            { id: "other_comments", label: "Filer Comments" },
            { id: "filing_status", label: "Filing Status" },
          ]
        : [
            { id: "date", label: "Date" },
            { id: "preparer", label: "Preparer" },
            { id: "last_name", label: "Last Name" },
            { id: "first_name", label: "First Name" },
            { id: "fed_due_ref", label: "Fed Due/Ref" },
            { id: "fed_payment_method", label: "Payment Method" },
            { id: "state_due_ref", label: "State Due/Ref" },
            { id: "state_payment_method", label: "Payment Method" },
            { id: "filing_status", label: "Status" },
            { id: "filer_comment", label: "Preparer Comment" },
            { id: "signed_consents_link", label: "Signed Consents" },
            { id: "payment_status", label: "Payment Status" },
            { id: "other_comments", label: "Filer Comment" },
            { id: "ack_status", label: "Ack. Status" },
            { id: "invoice", label: "INV No. / Amt" },
          ];
    if (showEditAction) {
      headers.push({ id: "", label: "Action" });
    }
    return headers;
  }, [showEditAction, categoryTab]);

  const handleInvoiceClick = useCallback(
    async (row) => {
      const invNo = row?.invoice_no ?? row?.invoiceNo ?? "";
      if (isBusiness) {
        const doc =
          findBusinessOfficeDocument(
            officeDocuments,
            row?.invoice_document_id ?? row?.invoiceDocumentId ?? invNo,
          ) || findBusinessOfficeDocument(officeDocuments, invNo);
        const link =
          getOfficeDocumentLink(doc) ||
          String(row?.invoice_document_link ?? row?.invoiceDocumentLink ?? "").trim();
        if (link) {
          setOfficeDocPreview({
            open: true,
            url: link,
            documentName: getOfficeDocumentName(doc) || invNo,
            clientDocumentId:
              doc?.client_document_id ?? doc?.clientDocumentId ?? "",
          });
          return;
        }
        Toaster.warning("Office document not found for this e-filing row.");
        return;
      }
      if (typeof onViewInvoice !== "function") return;
      if (!invNo && !row?.client_invoice_id) return;
      if (typeof ensureClientInvoices === "function") {
        ensureClientInvoices();
      }
      const inv =
        findInvoiceForSelect(clientInvoices, invNo) ||
        findInvoiceForSelect(
          clientInvoices,
          row?.client_invoice_id ?? row?.clientInvoiceId,
        );
      if (inv) {
        onViewInvoice(inv);
        return;
      }
      // Fallback: open with whatever identifiers we have on the e-filing row.
      onViewInvoice({
        invoice_no: invNo,
        client_invoice_id:
          row?.client_invoice_id ?? row?.clientInvoiceId ?? null,
        total: row?.invoice_amount ?? row?.invoiceAmount ?? null,
        billed_to: clientId,
      });
    },
    [
      onViewInvoice,
      ensureClientInvoices,
      clientInvoices,
      clientId,
      isBusiness,
      officeDocuments,
    ],
  );

  const tableData = useMemo(() => {
    const isBiz = categoryTab === "business";
    return (filteredRecords || []).map((r, i) => {
      const d =
        r.filing_date ||
        r.date ||
        r.created_at ||
        r.createdAt ||
        "";
      const dateLabel = d
        ? moment(d).isValid()
          ? moment(d).format("MM/DD/YYYY")
          : String(d)
        : "";

      const normalizePay = (raw) => {
        const v = String(raw ?? "").trim();
        return v.toLowerCase() === "paper check" ? "Check" : v;
      };

      const invoiceCell = (() => {
        const invNo = r.invoice_no ?? r.invoiceNo ?? "";
        const invAmt =
          r.invoice_amount != null
            ? formatMoneyDisplay(r.invoice_amount)
            : r.invoiceAmount != null
              ? formatMoneyDisplay(r.invoiceAmount)
              : "";
        const text = isBiz
          ? invNo
            ? String(invNo)
            : ""
          : invNo && invAmt
            ? `${invNo} - $${invAmt}`
            : invNo
              ? String(invNo)
              : invAmt
                ? `$${invAmt}`
                : "";
        if (!text) return <span className="efiling-nowrap">—</span>;
        if (!isBiz && typeof onViewInvoice !== "function") {
          return <span className="efiling-nowrap">{text}</span>;
        }
        return (
          <button
            type="button"
            className="efiling-invoice-link efiling-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              handleInvoiceClick(r);
            }}
          >
            {text}
          </button>
        );
      })();

      const enabledActions = {
        ...(showEditAction ? { edit: true } : {}),
        editStatus: (() => {
          if (canEditStatus) return true;
          const filingStatus = String(r.filing_status ?? r.status ?? "").trim();
          if (filingStatus === "Rejected") {
            return Boolean(
              clientData?.sales &&
                String(clientData.sales) === String(loggedInUserId),
            );
          }
          return false;
        })(),
      };

      const meta = {
        _efiling_id:
          r.efiling_id ??
          r.business_efiling_id ??
          r.id ??
          r.efilingId ??
          null,
        _raw: r,
        key:
          r.efiling_id ??
          r.business_efiling_id ??
          r.id ??
          `ef-${i}`,
        enabledActions,
      };

      // Table renders by Object.keys order — must match header column order.
      if (isBiz) {
        return {
          date: dateLabel,
          preparer:
            r.preparer_display_name ||
            r.preparer ||
            r.preparer_name ||
            "",
          entity_name: r.entity_name ?? r.entityName ?? "",
          business_type: r.business_type ?? r.businessType ?? r.type ?? "",
          state_filing: r.state_filing ?? r.stateFiling ?? "",
          federal_due: r.federal_due ?? r.federalDue ?? "",
          fed_payment_method: normalizePay(
            r.fed_payment_method ?? r.fedPaymentMethod ?? "",
          ),
          state_due: r.state_due ?? r.stateDue ?? "",
          state_payment_method: normalizePay(
            r.state_payment_method ?? r.statePaymentMethod ?? "",
          ),
          ca_estimate_payment:
            r.ca_estimate_payment ?? r.caEstimatePayment ?? "",
          ca_payment_method: normalizePay(
            r.ca_payment_method ?? r.caPaymentMethod ?? "",
          ),
          invoice: invoiceCell,
          invoice_amount: (() => {
            const amt =
              r.invoice_amount != null
                ? formatMoneyDisplay(r.invoice_amount)
                : r.invoiceAmount != null
                  ? formatMoneyDisplay(r.invoiceAmount)
                  : "";
            return amt ? `$${amt}` : "";
          })(),
          invoice_status:
            r.invoice_status ??
            r.invoiceStatus ??
            r.payment_status ??
            r.paymentStatus ??
            "",
          filer_comment: r.filer_comment ?? r.filerComment ?? "",
          signed_consents_link: (
            <EfilingSignedConsentsDisplay
              value={r.signed_consents_link ?? r.signedConsentsLink ?? ""}
            />
          ),
          password: r.password ?? r.consent_password ?? "",
          other_comments: r.other_comments ?? r.otherComments ?? "",
          filing_status: renderEfilingStatusBadge(
            r.filing_status ?? r.status ?? "",
          ),
          ...meta,
        };
      }

      return {
        date: dateLabel,
        preparer:
          r.preparer_display_name ||
          r.preparer ||
          r.preparer_name ||
          "",
        last_name: r.client_last_name ?? r.last_name ?? "",
        first_name: r.client_first_name ?? r.first_name ?? "",
        fed_due_ref: r.fed_due_ref ?? r.fedDueRef ?? "",
        fed_payment_method: normalizePay(
          r.fed_payment_method ?? r.fedPaymentMethod ?? "",
        ),
        state_due_ref: r.state_due_ref ?? r.stateDueRef ?? "",
        state_payment_method: normalizePay(
          r.state_payment_method ?? r.statePaymentMethod ?? "",
        ),
        filing_status: renderEfilingStatusBadge(
          r.filing_status ?? r.status ?? "",
        ),
        filer_comment: r.filer_comment ?? r.filerComment ?? "",
        signed_consents_link: (
          <EfilingSignedConsentsDisplay
            value={r.signed_consents_link ?? r.signedConsentsLink ?? ""}
          />
        ),
        payment_status: r.payment_status ?? r.paymentStatus ?? "",
        other_comments: r.other_comments ?? r.otherComments ?? "",
        ack_status: r.ack_status ?? r.ackStatus ?? "",
        invoice: invoiceCell,
        ...meta,
      };
    });
  }, [
    filteredRecords,
    showEditAction,
    canEditStatus,
    onViewInvoice,
    handleInvoiceClick,
    clientData,
    loggedInUserId,
    categoryTab,
  ]);

  const openEdit = useCallback(
    (row) => {
      const raw = row?._raw || row;
      setEditRow(raw);
      setEditForm(
        categoryTab === "business"
          ? recordToBusinessEditForm(raw)
          : recordToEditForm(raw),
      );
      setEditFormErrors({});
      setEditOpen(true);
    },
    [categoryTab],
  );

  const openStatusEdit = useCallback((row) => {
    const raw = row?._raw || row;
    const filingStatus = String(raw?.filing_status ?? raw?.status ?? "").trim();
    if (filingStatus === "Rejected" && !canEditStatus) {
      const isClientManager = clientData?.sales && String(clientData.sales) === String(loggedInUserId);
      if (!isClientManager) {
        Toaster.error("Only the assigned client manager can edit the status of a rejected e-filing.");
        return;
      }
    }
    setStatusEditRow(raw);
    setStatusEditForm({
      filing_status: filingStatus === "Rejected" ? "Pending" : (raw?.filing_status ?? raw?.status ?? "Pending"),
      other_comments: raw?.other_comments ?? raw?.otherComments ?? "",
    });
    setStatusEditFormErrors({});
    setStatusEditOpen(true);
  }, [clientData, loggedInUserId, canEditStatus]);

  const onTableAction = useCallback(
    (action, row) => {
      if (action === "edit") openEdit(row);
      if (action === "editStatus") openStatusEdit(row);
    },
    [openEdit, openStatusEdit],
  );

  const handleSaveEdit = async () => {
    const efilingId =
      editRow?.efiling_id ??
      editRow?.business_efiling_id ??
      editRow?.id ??
      editRow?.efilingId;
    if (!efilingId || !clientId) return;

    const validation =
      categoryTab === "business"
        ? validateBusinessEfilingForm(editForm, {
            skipStatusAndFilerComment: true,
          })
        : validateEditEfilingForm(editForm, clientFirst, clientLast, {
            skipStatusAndFilerComment: true,
          });
    if (!validation.valid) {
      setEditFormErrors(validation.errors);
      Toaster.warning("Please fill in all required fields.");
      return;
    }
    setEditFormErrors({});

    setEditSaving(true);
    try {
      const payload =
        categoryTab === "business"
          ? {
              ...buildBusinessEfilingPayload(
                editForm,
                {
                  clientId,
                  preparerEmployeeId: preparerInfo.preparerEmployeeId,
                  preparerDisplay: preparerInfo.preparerDisplay,
                  resolvedInvoice: editResolvedInvoice,
                  originalRecord: editRow,
                  invoiceTotalAmount,
                  officeDocument: editSelectedOfficeDocument,
                },
                { preserveStatusAndFilerComment: true },
              ),
              category: "business",
            }
          : buildEfilingUpdatePayload(
              editForm,
              {
                clientId,
                category: categoryTab,
                clientFirst,
                clientLast,
                preparerEmployeeId: preparerInfo.preparerEmployeeId,
                preparerDisplay: preparerInfo.preparerDisplay,
                resolvedInvoice: editResolvedInvoice,
                originalRecord: editRow,
                invoiceTotalAmount,
              },
              { preserveStatusAndFilerComment: true },
            );

      const res = await updateClientEfiling(efilingId, payload);
      const body = res?.data || {};
      if (res?.status === 200 && body.status === 1) {
        Toaster.success(body.message || "E-filing updated.");
        setEditOpen(false);
        loadRecords();
      } else {
        Toaster.error(
          body.message || body.error || "Could not update e-filing.",
        );
      }
    } catch (e) {
      Toaster.error(
        e?.response?.data?.message ||
          e?.message ||
          "Could not update e-filing.",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleSaveStatusEdit = async () => {
    const efilingId =
      statusEditRow?.efiling_id ??
      statusEditRow?.business_efiling_id ??
      statusEditRow?.id ??
      statusEditRow?.efilingId;
    if (!efilingId) return;

    const validation = validateEfilingStatusForm(statusEditForm);
    if (!validation.valid) {
      setStatusEditFormErrors(validation.errors);
      Toaster.warning("Please fill in all required fields.");
      return;
    }
    setStatusEditFormErrors({});

    setStatusEditSaving(true);
    try {
      const payload = {
        ...buildEfilingStatusUpdatePayload(statusEditForm),
        ...(categoryTab === "business" ? { category: "business" } : {}),
      };
      const res = await updateClientEfiling(efilingId, payload);
      const body = res?.data || {};
      if (res?.status === 200 && body.status === 1) {
        Toaster.success(body.message || "Status updated.");
        setStatusEditOpen(false);
        loadRecords();
      } else {
        Toaster.error(
          body.message || body.error || "Could not update status.",
        );
      }
    } catch (e) {
      Toaster.error(
        e?.response?.data?.message ||
          e?.message ||
          "Could not update status.",
      );
    } finally {
      setStatusEditSaving(false);
    }
  };

  const handleSaveCreate = async () => {
    if (!clientId) return;

    const validation =
      categoryTab === "business"
        ? validateBusinessEfilingForm(form)
        : validateCreateEfilingForm(form, clientFirst, clientLast);
    if (!validation.valid) {
      setFormErrors(validation.errors);
      Toaster.warning("Please fill in all required fields.");
      return;
    }
    setFormErrors({});

    setSaving(true);
    try {
      const payload =
        categoryTab === "business"
          ? {
              ...buildBusinessEfilingPayload(form, {
                clientId,
                preparerEmployeeId: preparerInfo.preparerEmployeeId,
                preparerDisplay: preparerInfo.preparerDisplay,
                resolvedInvoice,
                invoiceTotalAmount,
                officeDocument: selectedOfficeDocument,
              }),
              category: "business",
            }
          : {
              client_id: clientId,
              category: categoryTab,
              filing_date: form.filing_date,
              preparer_employee_id: preparerInfo.preparerEmployeeId ?? null,
              preparer_display_name: preparerInfo.preparerDisplay,
              client_first_name: clientFirst,
              client_last_name: clientLast,
              invoice_no: form.invoice_no || null,
              invoice_amount: resolvedInvoice
                ? invoiceTotalAmount(resolvedInvoice) || null
                : null,
              payment_status: form.payment_status_readonly || null,
              payment_method: form.payment_method_readonly || null,
              fed_due_ref: form.fed_due_ref || null,
              fed_payment_method: form.fed_payment_method || null,
              state_due_ref: form.state_due_ref || null,
              state_payment_method: form.state_payment_method || null,
              filing_status: form.filing_status || "Pending",
              filer_comment: form.filer_comment || null,
              signed_consents_link: form.signed_consents_link || null,
              client_mgr_verified: form.client_mgr_verified || null,
              other_comments: form.other_comments || null,
              ack_status: form.ack_status || null,
            };

      const res = await createClientEfiling(payload);
      const body = res?.data || {};
      if (res?.status === 200 && body.status === 1) {
        Toaster.success(body.message || "E-filing record saved.");
        setCreateOpen(false);
        loadRecords();
      } else {
        Toaster.error(body.message || body.error || "Could not save e-filing.");
      }
    } catch (e) {
      Toaster.error(
        e?.response?.data?.message ||
          e?.message ||
          "Could not save e-filing.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="client-details-efiling">
      {showCategoryTabs && (
        <Paper className="efiling-toolbar-card efiling-toolbar-card--category-only">
          <div className="efiling-type-tabs">
            <button
              type="button"
              className={`efiling-type-tab ${categoryTab === "individual" ? "is-active" : ""}`}
              onClick={() => setInternalCategoryTab("individual")}
            >
              Individual
            </button>
            <button
              type="button"
              className={`efiling-type-tab ${categoryTab === "business" ? "is-active" : ""}`}
              onClick={() => setInternalCategoryTab("business")}
            >
              Business
            </button>
          </div>
        </Paper>
      )}

      <div className="efiling-status-tabs">
        <div className="efiling-status-tabs-list">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`efiling-status-tab ${statusFilter === tab.id ? "is-active" : ""}`}
              onClick={() => setStatusFilter(tab.id)}
              disabled={loading || editSaving || statusEditSaving}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Button text="Create" handleClick={openCreate} />
      </div>

      <div className="efiling-table-wrap">
        {loading ? (
          <div className="efiling-loading">Loading…</div>
        ) : (
          <Table
            title=""
            header={tableHeader}
            data={tableData}
            EnableSno={false}
            actions={tableActions}
            onActionClick={onTableAction}
            restrictActionIcon={tableActions.length > 0}
            hiddenFields={["key", "_efiling_id", "_raw"]}
            aligncss="dashboard-table client-efiling-table"
          />
        )}
      </div>

      <EfilingEditModal
        open={editOpen}
        onClose={() => !editSaving && setEditOpen(false)}
        saving={editSaving}
        hideStatusAndFilerComment
        form={editForm}
        setForm={setEditForm}
        formErrors={editFormErrors}
        onClearError={(key) =>
          setEditFormErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          })
        }
        clientFirst={clientFirst}
        clientLast={clientLast}
        categoryTab={categoryTab}
        invoiceDropdown={invoiceDropdown}
        onInvoiceSelect={(v) => applyInvoiceSelection(v, "edit")}
        onSave={handleSaveEdit}
        clientId={clientId}
      />

      <EfilingDocumentPreviewModal
        open={officeDocPreview.open}
        sourceUrl={officeDocPreview.url}
        documentName={officeDocPreview.documentName}
        clientDocumentId={officeDocPreview.clientDocumentId}
        title="Invoice Preview"
        onClose={() =>
          setOfficeDocPreview({
            open: false,
            url: "",
            documentName: "",
            clientDocumentId: "",
          })
        }
      />

      <EfilingStatusEditModal
        open={statusEditOpen}
        onClose={() => !statusEditSaving && setStatusEditOpen(false)}
        saving={statusEditSaving}
        form={statusEditForm}
        setForm={setStatusEditForm}
        formErrors={statusEditFormErrors}
        onClearError={(key) =>
          setStatusEditFormErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          })
        }
        onSave={handleSaveStatusEdit}
        isRejectedStatus={
          statusEditRow?.filing_status === "Rejected" ||
          statusEditRow?.status === "Rejected"
        }
        isAdmin={canEditStatus}
      />

      <Modal
        title={null}
        open={createOpen}
        handleClose={() => !saving && setCreateOpen(false)}
        maxWidth
        className="efiling-create-modal"
      >
        <div className="efiling-modal-shell">
          <div className="efiling-modal-header-row">
            <h3 className="efiling-modal-title">Create E-filing</h3>
            <IconButton
              aria-label="close"
              className="efiling-modal-close-btn"
              onClick={() => !saving && setCreateOpen(false)}
              disabled={saving}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <p className="efiling-modal-category-line">
            Type:{" "}
            <strong>
              {categoryTab === "individual" ? "Individual" : "Business"}
            </strong>
          </p>
          {categoryTab === "business" ? (
            <BusinessEfilingFormFields
              form={form}
              setField={(key, value) => {
                clearFormError(key);
                setForm((s) => ({ ...s, [key]: value }));
              }}
              formErrors={formErrors}
              saving={saving}
              invoiceDropdown={invoiceDropdown}
              onInvoiceSelect={(v) => applyInvoiceSelection(v)}
              clientId={clientId}
              documentCategory={categoryTab}
              hideStatusAndFilerComment={!canEditStatus}
              canEditFilerComment={canEditStatus}
            />
          ) : (
          <Grid container spacing={2} className="efiling-form-grid">
            <Grid item xs={12} md={6}>
              <Labelbox
                type="datepicker"
                labelname="Date"
                value={form.filing_date || null}
                changeData={(v) => {
                  clearFormError("filing_date");
                  setForm((s) => ({ ...s, filing_date: v || "" }));
                }}
                required
                error={Boolean(formErrors.filing_date)}
                errmsg={formErrors.filing_date}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="select"
                labelname="INV No."
                dropdown={invoiceDropdown}
                value={form.invoice_no === "" ? "" : String(form.invoice_no)}
                changeData={(v) => {
                  clearFormError("invoice_no");
                  applyInvoiceSelection(v);
                }}
                required
                error={Boolean(formErrors.invoice_no)}
                errmsg={formErrors.invoice_no}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="efiling-textfield-wrap">
                <EfilingReadonlyField
                  label="Last name"
                  value={clientLast}
                  required
                  error={Boolean(formErrors.client_last)}
                  errmsg={formErrors.client_last}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="efiling-textfield-wrap">
                <EfilingReadonlyField
                  label="First name"
                  value={clientFirst}
                  required
                  error={Boolean(formErrors.client_first)}
                  errmsg={formErrors.client_first}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="text"
                labelname="Fed Due/Ref"
                value={form.fed_due_ref}
                changeData={(v) => {
                  clearFormError("fed_due_ref");
                  setForm((s) => ({ ...s, fed_due_ref: v }));
                }}
                required
                error={Boolean(formErrors.fed_due_ref)}
                errmsg={formErrors.fed_due_ref}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="select"
                labelname="F Payment Method"
                dropdown={PAYMENT_METHOD_OPTIONS}
                value={form.fed_payment_method || ""}
                changeData={(v) => {
                  clearFormError("fed_payment_method");
                  setForm((s) => ({ ...s, fed_payment_method: v }));
                }}
                required
                error={Boolean(formErrors.fed_payment_method)}
                errmsg={formErrors.fed_payment_method}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="text"
                labelname="State Due/Ref"
                value={form.state_due_ref}
                changeData={(v) => {
                  clearFormError("state_due_ref");
                  setForm((s) => ({ ...s, state_due_ref: v }));
                }}
                required
                error={Boolean(formErrors.state_due_ref)}
                errmsg={formErrors.state_due_ref}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="select"
                labelname="S Payment Method"
                dropdown={PAYMENT_METHOD_OPTIONS}
                value={form.state_payment_method || ""}
                changeData={(v) => {
                  clearFormError("state_payment_method");
                  setForm((s) => ({ ...s, state_payment_method: v }));
                }}
                required
                error={Boolean(formErrors.state_payment_method)}
                errmsg={formErrors.state_payment_method}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="text"
                labelname="Preparer comment"
                value={form.filer_comment}
                changeData={(v) => {
                  clearFormError("filer_comment");
                  setForm((s) => ({ ...s, filer_comment: v }));
                }}
                required
                error={Boolean(formErrors.filer_comment)}
                errmsg={formErrors.filer_comment}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="select"
                labelname="Client Mgr Verified"
                dropdown={CLIENT_MGR_VERIFIED_OPTIONS}
                value={form.client_mgr_verified || ""}
                changeData={(v) => {
                  clearFormError("client_mgr_verified");
                  setForm((s) => ({ ...s, client_mgr_verified: v }));
                }}
                required
                error={Boolean(formErrors.client_mgr_verified)}
                errmsg={formErrors.client_mgr_verified}
              />
            </Grid>
            {canEditStatus ? (
              <Grid item xs={12} md={6}>
                <Labelbox
                  type="text"
                  labelname="Filer comment"
                  value={form.other_comments}
                  changeData={(v) => {
                    clearFormError("other_comments");
                    setForm((s) => ({ ...s, other_comments: v }));
                  }}
                  error={Boolean(formErrors.other_comments)}
                  errmsg={formErrors.other_comments}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} md={6}>
              <Labelbox
                type="text"
                labelname="Ack. status"
                value={form.ack_status}
                changeData={(v) => {
                  clearFormError("ack_status");
                  setForm((s) => ({ ...s, ack_status: v }));
                }}
                required
                error={Boolean(formErrors.ack_status)}
                errmsg={formErrors.ack_status}
              />
            </Grid>
            <Grid item xs={12}>
              <EfilingConsentField
                clientId={clientId}
                value={form.signed_consents_link}
                onChange={(v) =>
                  setForm((s) => ({ ...s, signed_consents_link: v }))
                }
                disabled={saving}
                documentCategory={categoryTab}
              />
            </Grid>
          </Grid>
          )}

          <div className="efiling-modal-actions">
            <Button
              text="Cancel"
              ui="transparent"
              variant="outline"
              disable={saving}
              handleClick={() => setCreateOpen(false)}
            />
            <Button
              text={saving ? "Saving…" : "Save"}
              disable={saving || !isCreateFormValid}
              handleClick={handleSaveCreate}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
